import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getAllSkus } from '@/api/clarityLens'
import { ScalrTableColumn } from '@/components/customComponents'
import { pushNotification, useAppDispatch } from '@/store'
import { AllSkusResponseModel } from '@/types/clarityLens'
import { AllSkusModel } from '@/types/clarityLens/metrics/spendLens/priceHistory/allSkus.model'
import { areFilterParamsValid } from '@/utils/filterParams'

const ALL_SKUS_COLUMNS: ScalrTableColumn[] = [
  {
    header: 'SKU Name',
    accessorKey: 'skuName',
    type: 'text',
  },
  {
    header: 'Jan',
    accessorKey: 'January',
    type: 'money',
  },
  {
    header: 'Feb',
    accessorKey: 'February',
    type: 'money',
  },
  {
    header: 'Mar',
    accessorKey: 'March',
    type: 'money',
  },
  {
    header: 'Apr',
    accessorKey: 'April',
    type: 'money',
  },
  {
    header: 'May',
    accessorKey: 'May',
    type: 'money',
  },
  {
    header: 'Jun',
    accessorKey: 'June',
    type: 'money',
  },
  {
    header: 'Jul',
    accessorKey: 'July',
    type: 'money',
  },
  {
    header: 'Aug',
    accessorKey: 'August',
    type: 'money',
  },
  {
    header: 'Sep',
    accessorKey: 'September',
    type: 'money',
  },
  {
    header: 'Oct',
    accessorKey: 'October',
    type: 'money',
  },
  {
    header: 'Nov',
    accessorKey: 'November',
    type: 'money',
  },
  {
    header: 'Dec',
    accessorKey: 'December',
    type: 'money',
  },
]

export default function useAllSkus(
  startDate: string,
  endDate: string,
  branches: string,
  itemName: string
) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [isFetching, setIsFetching] = useState<boolean>(false)

  const isReady = useMemo(
    () =>
      areFilterParamsValid(startDate, endDate, branches) &&
      Boolean(itemName?.trim()),
    [startDate, endDate, branches, itemName]
  )
  const loading = !isReady || isFetching

  const [allSkusData, setAllSkusData] = useState<AllSkusModel[]>()

  const fetchAllData = useCallback(async () => {
    if (!isReady) {
      setIsFetching(false)
      return
    }
    setIsFetching(true)

    try {
      const { data } = await fetchAllSkusData(
        branches,
        startDate,
        endDate,
        itemName
      )

      setAllSkusData(data)
    } catch (err) {
      const errorInstance = new Error(
        err instanceof Error ? err.message : 'An unknown error occurred'
      )

      dispatch(
        pushNotification({
          type: 'error',
          title: t('common.errors.serverError'),
          description: errorInstance.message,
          duration: 5000,
        })
      )
    } finally {
      setIsFetching(false)
    }
  }, [isReady, branches, startDate, endDate, itemName])

  const refetch = useCallback(() => {
    console.log('Refetching sku breakdown information...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    allSkusData,
    allSkusColumns: ALL_SKUS_COLUMNS,
    loading,
    refetch,
  }
}

function convertAllSkusResponse(
  response: AllSkusResponseModel
): AllSkusModel[] {
  return Object.entries(response.all_skus).map(([skuName, months]) => ({
    skuName,
    ...months,
  }))
}

async function fetchAllSkusData(
  branches: string,
  startDate: string,
  endDate: string,
  itemName: string
): Promise<{
  data: AllSkusModel[]
}> {
  try {
    const response = await getAllSkus(branches, startDate, endDate, itemName)

    const data: AllSkusModel[] = convertAllSkusResponse(response)

    return {
      data,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}
